import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { IDocumentLinkItem } from 'app/core/interfaces/document.interface';
import { IWorkspaceList } from 'app/core/interfaces/home.interface';
import { IUserInvite, IUserInviteResponse, IUserResponsed, IValidationEmails } from 'app/core/interfaces/user.interface';
import { INewWorkspaceResponse, IWorkspace, IWorkspaceEdit, IWorkspaceListResponse, IWorkspaceWithDocument, UserWorkspaceAccessRequest } from 'app/core/interfaces/workspace.interface';
import { IPaginatedResponse } from 'app/shared/interfaces/IPagination';
import { map, Observable, tap } from 'rxjs';

const baseUrl = environment.apiHost;
@Injectable({
  providedIn: 'root'
})
export class WorkspaceService {
  private readonly http = inject(HttpClient);

  getWorkspaces(
    page: number = 1,
    size: number = 5,
    query?: string,
    activeOnly?: boolean
  ): Observable<{ items: IWorkspaceListResponse[]; total: number }> {
    let params = new HttpParams()
      .set('page', String(page))
      .set('size', String(size));

    const q = query?.trim();
    if (q) params = params.set('query', q);
    // QBRAIN-492: opt-in para excluir workspaces desactivados (p. ej. filtro de Anuncios).
    if (activeOnly) params = params.set('activeOnly', 'true');

    return this.http
      .get<IPaginatedResponse<IWorkspaceListResponse> | IWorkspaceListResponse[]>(
        `${baseUrl}/Workspace/listWorkspaces`,
        { params }
      )
      .pipe(
        map((resp: any) => {
          if (Array.isArray(resp)) {
            return { items: resp as IWorkspaceListResponse[], total: resp.length };
          }

          const items: IWorkspaceListResponse[] = resp?.items ?? [];
          const total: number = (
            resp?.totalItems ?? 
            resp?.total ?? 
            resp?.totalCount ?? 
            items.length
          );
          return { items, total };
        })
      );
  }


  getListUsersPendingWorkspaces(
    page: number = 1,
    size: number = 5,
    workspaceID: string,
    query?: string
  ): Observable<{ items: IUserInviteResponse[]; total: number }> {
    let params = new HttpParams()
      .set('page', String(page))
      .set('size', String(size))
      .set('WorkspaceId', String(workspaceID));

    const q = query?.trim();
    if (q) params = params.set('query', q);

    return this.http
      .get<IPaginatedResponse<IUserInviteResponse> | IUserInviteResponse[]>(
        `${baseUrl}/Workspace/listPendingUsers`,
        { params }
      )
      .pipe(
        map((resp: any) => {
          if (Array.isArray(resp)) {
            return { items: resp as IUserInviteResponse[], total: resp.length };
          }

          const items: IUserInviteResponse[] = resp?.items ?? [];
          const total: number = (
            resp?.totalItems ?? 
            resp?.total ?? 
            resp?.totalCount ?? 
            items.length
          );
          return { items, total };
        })
      );
  }

  getElegibleUsersForWorkspace(
    page: number = 1,
    size: number = 3,
    workspaceID?: string,
    query?: string,
  ): Observable<{ items: IUserResponsed[]; total: number }> {
    let params = new HttpParams()
      .set('page', String(page))
      .set('size', String(size))

      if(workspaceID) params = params.set('WorkspaceId', String(workspaceID));

    const q = query?.trim();
    if (q) params = params.set('query', q);

    return this.http
      .get<IPaginatedResponse<IUserResponsed> | IUserResponsed[]>(
        `${baseUrl}/Workspace/elegibleUsers`,
        { params }
      )
      .pipe(
        map((resp: any) => {
          if (Array.isArray(resp)) {
            return { items: resp as IUserResponsed[], total: resp.length };
          }

          const items: IUserResponsed[] = resp?.items ?? [];
          const total: number = (
            resp?.totalItems ?? 
            resp?.total ?? 
            resp?.totalCount ?? 
            items.length
          );
          return { items, total };
        })
      );
  }

  getElegibleDocumentsForWorkspace(
    page: number = 1,
    size: number = 5,
    workspaceID: string,
    query?: string,
  ): Observable<{ items: IDocumentLinkItem[]; total: number }> {
    let params = new HttpParams()
      .set('page', String(page))
      .set('size', String(size))

      if(workspaceID) params = params.set('workspaceID', String(workspaceID));

    const q = query?.trim();
    if (q) params = params.set('query', q);

    return this.http
      .get<IPaginatedResponse<IDocumentLinkItem> | IDocumentLinkItem[]>(
        `${baseUrl}/Workspace/elegibleDocuments`,
        { params }
      )
      .pipe(
        map((resp: any) => {
          if (Array.isArray(resp)) {
            return { items: resp as IDocumentLinkItem[], total: resp.length };
          }

          const items: IDocumentLinkItem[] = resp?.items ?? [];
          const total: number = (
            resp?.totalItems ?? 
            resp?.total ?? 
            resp?.totalCount ?? 
            items.length
          );
          return { items, total };
        })
      );
  }
  
  validationEmails(payload: IValidationEmails): Observable<IUserInviteResponse[]> {
    return this.http
      .post<IUserInviteResponse[]>(`${baseUrl}/Workspace/validateEmail`, payload);
  }

  getByIdWorkspaces(workspaceId: string): Observable<IWorkspaceList> {
    let params = new HttpParams().set('ID', workspaceId);
    return this.http.get<IWorkspaceList>(`${baseUrl}/Workspace/get`, { params });
  }

    updateStatusWorkspace(workspaceId: string, status: boolean): Observable<boolean> {
    let params = new HttpParams().set('WorkspaceID', workspaceId).set('status', status);
    console.log(status);
    return this.http.patch<boolean>(`${baseUrl}/Workspace/updateStatus`, {}, { params });
  }

  postWorkspace(items: IWorkspace): Observable<INewWorkspaceResponse> {
    return this.http.post<INewWorkspaceResponse>(`${baseUrl}/Workspace/create`, items);
  }

  updateWorkspaceWithDocument(items: IWorkspaceWithDocument, id: string): Observable<boolean> {
    const fd = new FormData();
    fd.append(`ID`, id);
    fd.append(`name`, items.workspace.name);
    fd.append(`description`, items.workspace.description);

    items.files?.forEach((it, i) => {
      if (it.category != undefined && it.category !== '') {
        fd.append(`Files[${i}].category`, it.category);
      }
      fd.append(`Files[${i}].file`, it.file);
      fd.append(`Files[${i}].name`, it.name);
    });

    return this.http.patch(`${baseUrl}/Workspace/updatePlus`, fd)
    .pipe(
      tap(resp => {
        console.log("Respuesta del backend: ",resp);
      }),
      map(() => true)
    );
  }

  updateWorkspace(items: IWorkspaceEdit): Observable<void> {
    return this.http.patch<void>(`${baseUrl}/Workspace/update`, items);
  }

  deleteWorkspace(workspaceId: string): Observable<void> {
    const params = new HttpParams().set('ID', workspaceId);
    return this.http.patch<void>(`${baseUrl}/Workspace/delete`, null, { params });
  }

  linkDocumentsToWorkspace(workspaceID: string, documentsID: string[]): Observable<boolean> {
    const payload = {
      workspaceID: workspaceID,
      documentsID: documentsID
    };
    return this.http.post<boolean>(`${baseUrl}/Workspace/linkDocuments`, payload);
  }

    unlinkUserstoWorkspace(workspaceID: string, documentsID: string[]): Observable<boolean> {
    const payload = {
      listWorkspace: workspaceID,
      listUsers: documentsID
    };
    return this.http.post<boolean>(`${baseUrl}/Workspace/unlinkUsers`, payload);
  }

  linkUserstoWorkspaces(payload: UserWorkspaceAccessRequest): Observable<boolean> {
    return this.http.post<boolean>(`${baseUrl}/Workspace/linkUsers`, payload);
  }

  deleteInvitation(email: string): Observable<boolean> {
    const data = {
      email: email
    };
    return this.http.patch(`${baseUrl}/User/deleteInvitation`, data)
      .pipe(
        map(() => true)
      );
  }
}